/**
 * ListRequestTransformerTask - трансформує параметри списку в query параметри для API
 *
 * Обробляє:
 * - Пагінацію (page, limit)
 * - Сортування (sort={"field":"order"})
 * - Фільтри (filters={"and":[...]})
 * - Пошук (q=пошуковий_запит)
 * - Трансформацію IRI значень для select полів з ресурсами
 * - Мультивибір: масив значень → оператор `in` (`responsibles.uuid in [...]`)
 * - Статус-довідник (`statusResource`): фільтр за `status.code`
 * - Телефон: введене зводиться до цифр — «+38 (067) 123…» знаходить «38067123…»
 */

interface FilterCondition {
    field: string
    op: string
    value: any
}

interface Filters {
    and?: FilterCondition[]
}

/**
 * Форматує дату у рядок формату YYYY-MM-DD
 */
const formatDateString = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

/**
 * Форматує дату і час у рядок формату YYYY-MM-DD HH:mm:ss
 */
const formatDateTimeString = (date: Date): string => {
    const dateStr = formatDateString(date)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${dateStr} ${hours}:${minutes}:${seconds}`
}

/**
 * Трансформує значення дати/часу для фільтрації з оператором between
 * value - масив з двома dayjs об'єктами [від, до]
 */
const transformDateValue = (fieldType: string, value: any): { op: string; value: any } => {
    // value завжди масив з двома dayjs значеннями [від, до]
    if (!Array.isArray(value) || value.length !== 2) {
        return { op: '=', value }
    }

    const [startValue, endValue] = value

    // Перевіряємо чи це dayjs об'єкти (мають метод toDate або format)
    const startDate = startValue?.toDate ? startValue.toDate() : new Date(startValue)
    const endDate = endValue?.toDate ? endValue.toDate() : new Date(endValue)

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return { op: '=', value }
    }

    if (fieldType === 'datetime') {
        // Для datetime: форматуємо з часом
        // Приклад: ["2025-10-31 13:08:00","2025-10-31 13:08:16"]
        return {
            op: 'between',
            value: [formatDateTimeString(startDate), formatDateTimeString(endDate)],
        }
    } else {
        // Для date: форматуємо тільки дату
        // Приклад: ["2025-10-31","2025-11-01"]
        return {
            op: 'between',
            value: [formatDateString(startDate), formatDateString(endDate)],
        }
    }
}

/**
 * Значення select-поля → те, за чим фільтрує бек (`primaryField`, за
 * замовчуванням `uuid`). `undefined` — значення не розпізнано.
 */
const primaryValueOf = (value: any, primaryField: string): any => {
    if (typeof value === 'object' && value !== null) {
        // Якщо primaryField - 'uuid', а в об'єкті немає поля uuid, але є @id - витягуємо з IRI
        if (primaryField === 'uuid' && !value.uuid && value['@id']) {
            const iriParts = String(value['@id']).split('/')
            return iriParts[iriParts.length - 1]
        }

        // Об'єкт сутності (наприклад, {"@id": "...", "code": "demow", "uuid": "..."}):
        // беремо значення з поля primaryField, інакше — uuid або id
        return value[primaryField] ?? value.uuid ?? value.id
    }

    // IRI-рядок (наприклад, "/services/lists/truck_types/demow") — останній сегмент
    if (typeof value === 'string' && value.startsWith('/')) {
        const iriParts = value.split('/')
        return iriParts[iriParts.length - 1]
    }

    return value
}

/**
 * Масив значень фільтра → умова: один елемент — `=`, кілька — `in`.
 * Порожній вибір — `null`: умови немає зовсім, а не «нічого не знайти».
 */
const conditionForList = (field: string, values: any[]): { field: string; op: string; value: any } | null => {
    const list = values.filter((item) => item !== undefined && item !== null && item !== '')

    if (list.length === 0) return null

    return list.length === 1 ? { field, op: '=', value: list[0] } : { field, op: 'in', value: list }
}

/** Лише цифри: телефони в базі нормалізовані саме так (`ContactDataNormalizer`). */
const phoneDigits = (value: unknown): unknown => {
    if (typeof value !== 'string') return value

    const digits = value.replace(/\D/g, '')

    return digits !== '' ? digits : value
}

/**
 * Визначає оператор та трансформує значення на основі типу поля.
 * `null` — умови немає (порожній мультивибір).
 */
const transformFilterValue = (fieldConfig: any, value: any): { field: string; op: string; value: any } | null => {
    const name: string = fieldConfig?.name || ''

    // Статус-довідник (клієнти, перевізники, справи): у записі статус — об'єкт
    // рядка довідника, а плашки й поле панелі оперують кодом
    if (fieldConfig?.type === 'status' && typeof fieldConfig?.statusResource === 'string') {
        const field = `${name}.code`

        if (Array.isArray(value)) return conditionForList(field, value.map((item) => primaryValueOf(item, 'code')))

        return { field, op: '=', value: primaryValueOf(value, 'code') }
    }

    // Якщо поле не є select з ресурсом
    if (fieldConfig?.type !== 'select' || !fieldConfig?.resource) {
        const fieldType = fieldConfig?.type

        // Обробка полів типу date та datetime
        if (fieldType === 'date' || fieldType === 'datetime') {
            const transformed = transformDateValue(fieldType, value)
            return {
                field: name,
                op: transformed.op,
                value: transformed.value,
            }
        }

        // Мультивибір із переліку варіантів (без ресурсу)
        if (fieldType === 'select' && Array.isArray(value)) return conditionForList(name, value)

        // Визначаємо оператор на основі типу поля
        const op = determineOperator(fieldConfig)

        let prepared = fieldType === 'phone' ? phoneDigits(value) : value

        if (typeof fieldConfig?.filterValueTransform === 'function' && typeof prepared === 'string') {
            prepared = fieldConfig.filterValueTransform(prepared)
        }

        return {
            field: name,
            op,
            value: op === 'like' ? `%${prepared}%` : prepared,
        }
    }

    // Визначаємо primaryField:
    // 1. З конфігурації поля (якщо явно вказано)
    // 2. За замовчуванням 'uuid'
    const primaryField = fieldConfig.primaryFieldCode || 'uuid'

    // Формуємо поле для фільтрації: fieldName.primaryField (наприклад, "type.code" або "truck.uuid")
    const filterField = `${name}.${primaryField}`

    // Мультивибір (`isFilterMultiple`): «Відповідальний» — будь-хто з обраних
    if (Array.isArray(value)) {
        return conditionForList(
            filterField,
            value.map((item) => primaryValueOf(item, primaryField))
        )
    }

    // Інші випадки (не об'єкт і не IRI) - використовуємо значення як є
    if ((typeof value !== 'object' || value === null) && !(typeof value === 'string' && value.startsWith('/'))) {
        return {
            field: name,
            op: '=',
            value,
        }
    }

    return {
        field: filterField,
        op: '=',
        value: primaryValueOf(value, primaryField),
    }
}

/**
 * Визначає оператор порівняння на основі типу поля
 */
const determineOperator = (fieldConfig: any): string => {
    // Якщо явно вказано оператор у конфігурації поля
    if (fieldConfig?.filterOperator) {
        return fieldConfig.filterOperator
    }

    // Якщо в налаштуваннях поля вказано isFilterLikeSearch - використовуємо LIKE
    if (fieldConfig?.settings?.isFilterLikeSearch === true) {
        return 'like'
    }

    // Для текстових полів використовуємо LIKE для часткового пошуку
    const textTypes = ['text', 'string', 'textarea', 'email', 'phone', 'mask']
    if (textTypes.includes(fieldConfig?.type)) {
        return 'like'
    }

    // Для чисел, дат та інших типів - точне співпадіння
    return '='
}

export const ListRequestTransformerTask = (params: any, listContext?: any) => {
    const { filter = {}, sorter = {}, pagination = {}, search } = params

    const result: Record<string, any> = {}

    // Опис полів ресурсу — з нього беремо тип поля, щоб обрати оператор
    const resourceFields = listContext?.props?.resource?.fields || {}

    // Обробка пагінації
    if (pagination.current !== undefined) {
        result['page'] = pagination.current
    }
    if (pagination.pageSize !== undefined) {
        result['limit'] = pagination.pageSize
    }

    // Обробка сортування
    if (sorter.key && sorter.order) {
        const sortOrder = sorter.order === 'ascend' ? 'ASC' : 'DESC'
        result['sort'] = JSON.stringify({ [sorter.key]: sortOrder })
    }

    // Обробка фільтрів
    // Структура: filters={"and":[filter1, filter2, ...]}
    // Пошук виноситься окремо в параметр q
    const filters: Filters = {}
    const andConditions: FilterCondition[] = []

    // Спочатку обробляємо звичайні фільтри (йдуть в AND умови)
    Object.keys(filter).forEach((key) => {
        const value = filter[key]

        // Пропускаємо undefined та null значення
        if (value === undefined || value === null) {
            return
        }

        // Отримуємо конфігурацію поля
        const fieldConfig = { ...resourceFields[key], name: key }

        // Всі фільтри додаємо в AND умови
        const transformed = transformFilterValue(fieldConfig, value)

        // Порожній мультивибір — умови немає
        if (transformed === null) return

        andConditions.push({
            field: transformed.field,
            op: transformed.op,
            value: transformed.value,
        })
    })

    // Обробка пошуку - виносимо в окремий параметр q
    // Backend сам обробляє пошук по searchFields
    if (search && typeof search === 'string' && search.trim() !== '') {
        result['q'] = search.trim()
    }

    // Додаємо фільтри в результат
    // Структура: {"and":[filter1, filter2, ...]}
    if (andConditions.length > 0) {
        filters.and = andConditions
        result['filters'] = JSON.stringify(filters)
    }

    // Незмінний обсяг сторінки, який не виражається пласким `prefilter`
    // (гілка `or`, умова `in`): ресурс дає `list.paramsScope` — і він діє
    // скрізь, де будується запит списку: таблиця, лічильники рядка статусів,
    // канбан, експорт («Рейси» заявок, `SPEC-021` §8.2).
    //
    // ⚠️ Не через власний `list.searchParamsTransformer` ресурсу: ядровий
    // `List` на кожен рендер зливає в пропси списку типовий `ListConfig`
    // (`listContext.mergeToInstance`) і повертає трансформер на цей
    const scope = listContext?.props?.resource?.list?.paramsScope

    return typeof scope === 'function' ? scope(result) : result
}
